/**
 * Navigation reference — the single source of truth for the app's menu.
 *
 * Derived from the design reference's `menuData` (RawHTMLDesign/layouts/*.html)
 * and correlated to real pages via RawHTMLDesign/PageDesignMap.json. Every leaf
 * records the page's **id** (Design-Studio page GUID), its **slug** (the route
 * path — navigation routes by slug, never by page id), and its **name** (label).
 *
 * The layout navbar is driven entirely from this file. When a page component is
 * generated (ui-components-from-reference), its route is registered on the slug
 * declared here; until then the slug falls through to the 404 catch-all so the
 * nav never dead-ends. Keep this file in sync — do not hand-maintain the menu
 * inline in the layout.
 */

export const menu = [
  {
    label: 'Patient Journey',
    icon: 'fa fa-heartbeat',
    children: [
      // Ticket 7980: Onboarding is deliberately absent from the menu. The wizard
      // is entered from the dashboard only ("Start My Smile Assessment" /
      // "Continue Onboarding" / "Add Family Member"), because those entry points
      // declare who is being onboarded — a bare menu link cannot. The route
      // (/onboarding/patient-profile-setup, page a4ca6365-7738-41ef-bb67-dfb7269d0023)
      // stays registered in App.jsx; only the nav entry is withdrawn.
      {
        label: 'Dashboard',
        icon: 'fa fa-home',
        slug: '/patient-care/dashboard',
        roles: ['user'],
        pageId: 'bc03f356-4a56-4a4a-8912-f9c39eaa8915',
      },
      {
        label: 'My Smile Review',
        icon: 'fa fa-camera-retro',
        slug: '/patient-care/my-smile-review',
        roles: ['user'],
        pageId: '23e33fb6-d303-4ee6-bf93-0b7c9ca268a5',
      },
      {
        label: 'Assessment Report',
        icon: 'fa fa-file-medical',
        slug: '/patient-care/assessment-report',
        roles: ['user'],
        pageId: 'e93c5300-9570-4494-be5e-ae996863a334',
      },
      {
        label: 'Request Consultation',
        icon: 'fa fa-calendar-plus',
        slug: '/patient-care/consultation-request',
        roles: ['user'],
        pageId: '8f716e04-cbdb-4bad-858a-41494b3c10f6',
      },
      {
        label: 'Consultation Scheduling',
        icon: 'fa fa-clock',
        slug: '/patient-care/consultation-scheduling',
        roles: ['user'],
        pageId: '257b6f69-cacd-4e0e-8840-e0ae00d87bf2',
      },
      {
        label: 'Manage Appointment',
        icon: 'fa fa-calendar-check',
        slug: '/patient-care/manage-appointment',
        roles: ['user'],
        pageId: '31f15ded-1b6d-46a1-93b5-f339c3a897ab',
      },
      {
        label: 'Account Management',
        icon: 'fa fa-user-cog',
        slug: '/account/account-management',
        roles: ['user'],
        pageId: '4cbe3bd6-1c62-4fcf-817c-30809598b2ba',
      },
    ],
  },
  {
    label: 'Clinical Operations',
    icon: 'fa fa-hand-holding-medical',
    children: [
      // Context-dependent clinical workspaces (Dentist Assessment Review,
      // Dental Monitoring and the Chair-side Workspace) are deliberately NOT in
      // the nav: they require a selected patient/case and are entered from the
      // Clinical Review, Appointment and Treatment dashboards. Bare visits
      // redirect to their entry dashboard (see each page).

      {
        // Every role starts here — the person comes before the work.
        label: 'Patient Lookup',
        icon: 'fa fa-users',
        slug: '/clinical-operations/patient-lookup',
        roles: ['receptionist', 'dentist', 'admin'],
        pageId: 'bc300e45-c2f1-4692-bfe4-105bf3729096',
      },
      {
        label: 'Clinical Review Dashboard',
        icon: 'fa fa-clipboard-check',
        slug: '/clinical-operations/clinical-review-dashboard',
        roles: ['dentist', 'admin'],
        pageId: '7e021e50-401c-4069-aa48-88e0609908c4',
      },
      {
        label: 'Appointment Dashboard',
        icon: 'fa fa-calendar-day',
        slug: '/clinical-operations/appointment-dashboard',
        roles: ['receptionist', 'admin'],
        pageId: 'c3e68d14-af2d-427f-b286-ae0114c07567',
      },
      /*
       * Clinical Smile Review is deliberately NOT in this nav (issue 8441,
       * BR-CSR-02): it is a patient-contextual workspace and must not operate
       * as a standalone workflow outside the Workbench. It is reached from a
       * patient's Manage menu on the Workbench Dashboard, which is where the
       * patient is chosen.
       *
       * The queue function the old standalone page served — being the place an
       * unreviewed submission cannot hide — is the Workbench's: smile-review
       * submissions and scan alerts raise Workbench items, so an unread scan
       * still surfaces without a second cross-patient page to hold it.
       */
      {
        label: 'Workbench Dashboard',
        icon: 'fa fa-tooth',
        slug: '/clinical-operations/workbench-dashboard',
        roles: ['dentist', 'admin'],
        pageId: 'e5820336-dc39-4caf-86b6-2cab54f06001',
      },
      {
        // 8786: insurance is out of this release; the page is Billing Management.
        label: 'Billing Dashboard',
        icon: 'fa fa-file-invoice-dollar',
        slug: '/clinical-operations/billing-management',
        roles: ['receptionist', 'admin'],
        pageId: 'a44bb5c2-a695-416d-98a5-077ebd14fecb',
      },
    ],
  },
  {
    /*
     * Twelve entries is too many to scan as a flat list, and the order they
     * accumulated in was the order they were built in — People sat between
     * Practice and AI for no reason a user could infer.
     *
     * They are now grouped by the question the admin arrived with, commonest
     * first: who works here (the daily work — a starter, a leaver, a wrong
     * role), then how the practice is configured, then oversight of the
     * detector, then individual cases. Within Practice the order is the
     * dependency chain — services are defined, then packaged, then priced —
     * so following it top to bottom is a working setup sequence.
     *
     * `section` is declared on EVERY entry, not only the first of a run: the
     * heading is drawn from the ROLE-FILTERED list, so a section whose entries
     * are all hidden leaves no orphan heading — and a section whose first entry
     * alone is hidden still gets its heading from whichever entry survives.
     */
    label: 'Administration',
    icon: 'fa fa-cogs',
    children: [
      {
        section: 'People & Access',
        label: 'User Account Management',
        icon: 'fa fa-users-cog',
        slug: '/administration/user-account-management',
        roles: ['admin'],
        pageId: 'de2008e5-e1da-4796-bf85-d60fd7d28971',
      },
      {
        section: 'People & Access',
        label: 'Role & Assignment Management',
        icon: 'fa fa-user-shield',
        slug: '/administration/role-assignment-management',
        roles: ['admin'],
        pageId: 'ed06f321-ad29-42e8-ad5a-ff192028b2ae',
      },
      {
        section: 'People & Access',
        label: 'Security Settings',
        icon: 'fa fa-lock',
        slug: '/administration/security-settings',
        roles: ['admin'],
        pageId: '231eacc8-3d47-406a-bd20-92ff89c351d1',
      },
      {
        // Issue 8221: Clinic Settings became the Practice Management module.
        // The route is unchanged so existing links and bookmarks still work.
        section: 'Practice Setup',
        label: 'Practice Management',
        icon: 'fa fa-hospital',
        slug: '/administration/clinic-settings',
        roles: ['admin'],
        pageId: 'a8728b90-9282-404e-95f2-8ac468803455',
      },
      {
        section: 'Practice Setup',
        label: 'Product & Service Catalogue',
        icon: 'fa fa-boxes-stacked',
        slug: '/administration/product-service-catalogue',
        roles: ['admin'],
        pageId: '8686344a-916a-4885-8382-213c3415d471',
      },
      {
        section: 'Practice Setup',
        label: 'Treatment Packages',
        icon: 'fa fa-briefcase-medical',
        slug: '/administration/treatment-packages',
        roles: ['admin'],
        pageId: 'c7b1f2a4-9d3e-4a51-8f62-4e0a9b1c2d34',
      },
      {
        // Issue 8415: an independent module in the Admin workspace. Access is
        // the Module Permissions Matrix's decision (issue 8416); the role list
        // here only decides who is shown the link.
        section: 'Practice Setup',
        label: 'ADA Practice Fee Management',
        icon: 'fa fa-file-invoice-dollar',
        slug: '/administration/ada-practice-fee-management',
        roles: ['admin'],
        pageId: 'ada-practice-fee-management',
      },
      {
        section: 'Practice Setup',
        label: 'Appointment Type Settings',
        icon: 'fa fa-list-check',
        slug: '/administration/appointment-type-settings',
        roles: ['admin'],
        pageId: 'aadeef0b-8d6b-4829-8cb4-19cad1c1b762',
      },
      {
        // Realignment 4.5: patient-facing advice the clinic has approved.
        // Dentists see it too — only an orthodontist can approve an article.
        section: 'Clinical Content',
        label: 'Troubleshooting Library',
        icon: 'fa fa-life-ring',
        slug: '/administration/troubleshooting-library',
        roles: ['dentist', 'admin'],
      },
      {
        // The detector's oversight, in the order it is exercised: the rules
        // that govern learning, the improvements it proposes, then whether
        // enough confirmed data has accumulated to train on.
        section: 'AI Oversight',
        label: 'AI Learning Rules',
        icon: 'fa fa-sliders-h',
        slug: '/administration/ai-learning-rules-configurator',
        roles: ['admin'],
        pageId: 'b4e9b949-28f0-4dd1-8980-83bbbb850110',
      },
      {
        section: 'AI Oversight',
        label: 'AI Improvements Review Workspace',
        icon: 'fa fa-graduation-cap',
        slug: '/administration/ai-improvements-review-workspace',
        roles: ['admin'],
        pageId: '89effc98-7508-4d90-aec4-57fc776cf2a5',
      },
      {
        section: 'AI Oversight',
        label: 'AI Training Readiness',
        icon: 'fa fa-chart-line',
        slug: '/administration/ai-training-readiness',
        roles: ['admin'],
      },
      {
        section: 'Cases',
        label: 'Admin Client Case Management',
        icon: 'fa fa-folder-open',
        slug: '/administration/admin-client-case-management',
        roles: ['admin'],
        pageId: '1bb0cf0e-5c1b-4a0b-8e2e-22dc8e77b51a',
      },
    ],
  },
  {
    label: 'Communication',
    icon: 'fa fa-comments',
    children: [
      {
        label: 'Conversation Inbox',
        icon: 'fa fa-inbox',
        slug: '/communication/conversation-inbox',
        roles: ['receptionist', 'dentist', 'admin'],
        pageId: '55dc61ca-8fd6-4025-95ae-8bb71e5606b1',
      },
      {
        label: 'Manual Notification Composer',
        icon: 'fa fa-paper-plane',
        slug: '/administration/manual-notification-composer',
        roles: ['dentist', 'admin'],
        pageId: '9bceeb3a-9b5e-4893-94b3-c566c0804a62',
      },
      {
        label: 'Message History Review',
        icon: 'fa fa-history',
        slug: '/administration/message-history-review',
        roles: ['admin'],
        pageId: '3dba703e-a40f-4676-8912-7b34e1ada54d',
      },
      {
        // Realignment 1.5: what the Koral Assistant may answer, and what it learns from the care team.
        label: 'Assistant Knowledge',
        icon: 'fa fa-book-medical',
        slug: '/administration/assistant-knowledge',
        roles: ['admin'],
      },
      {
        // Realignment 3.5: the clauses every treatment consent closes with.
        label: 'Treatment Consent',
        icon: 'fa fa-file-signature',
        slug: '/administration/treatment-consent',
        roles: ['admin'],
      },
      {
        // Realignment L6 follow-up: draft and approve the optional data-use permission wording.
        label: 'Data-Use Permission Wording',
        icon: 'fa fa-user-shield',
        slug: '/administration/data-use-wording',
        roles: ['admin'],
      },
    ],
  },
  {
    label: 'Reporting',
    icon: 'fa fa-chart-line',
    children: [
      {
        label: 'Clinic Reports Dashboard',
        icon: 'fa fa-chart-bar',
        slug: '/reporting/clinic-reports-dashboard',
        roles: ['dentist', 'admin'],
        pageId: '18620318-8404-4797-8196-2ae9603182b8',
      },
    ],
  },
];

/** Flat list of every leaf menu entry ({ label, icon, slug, pageId }). */
export const menuLeaves = menu.flatMap((group) => group.children ?? [group]);

export default menu;
